import { ApiError, ApiRequestError, ApiResponse } from '../types/api.types';

const TOKEN_STORAGE_KEY = 'reportAccessToken';

export const CLIENT_TYPE_HEADER = 'X-Client-Type';
export const CLIENT_TYPE_WEB = 'WEB';

type AuthExpiryListener = () => void;

export class ApiService {
  private baseUrl: string;
  private accessToken: string | null = null;
  private authExpiryListeners: Set<AuthExpiryListener> = new Set();

  constructor() {
    this.baseUrl = (import.meta.env.VITE_API_URL || '') + '/api';
    try {
      this.accessToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      this.accessToken = null;
    }
  }

  getAuthToken(): string | null {
    return this.accessToken;
  }

  setAuthToken(token: string | null): void {
    this.accessToken = token;
    try {
      if (token) {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      } else {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      }
    } catch {
      // sessionStorage unavailable (SSR, private mode) — token stays in memory only
    }
  }

  onAuthExpired(listener: AuthExpiryListener): () => void {
    this.authExpiryListeners.add(listener);
    return () => this.authExpiryListeners.delete(listener);
  }

  private notifyAuthExpired(): void {
    this.authExpiryListeners.forEach((listener) => listener());
  }

  private getHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [CLIENT_TYPE_HEADER]: CLIENT_TYPE_WEB,
    };
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }
    // `extra` wygrywa — pozwala dorzucić ad-hoc Authorization (quick-access)
    // bez zmieniania sesji w sessionStorage.
    return { ...headers, ...extra };
  }

  private hasAuthorizationOverride(
    headers?: Record<string, string>,
  ): boolean {
    return Object.keys(headers ?? {}).some(
      (header) => header.toLowerCase() === 'authorization',
    );
  }

  private invalidateStoredAuthIfExpired(
    response: Response,
    errorData: ApiError,
    hasAuthorizationOverride: boolean,
  ): void {
    if (
      response.status === 401 &&
      errorData.error === 'TOKEN_EXPIRED' &&
      !hasAuthorizationOverride
    ) {
      this.setAuthToken(null);
      this.notifyAuthExpired();
    }
  }

  private async parseErrorBody(response: Response): Promise<ApiError> {
    try {
      return (await response.json()) as ApiError;
    } catch {
      return {
        message: `Błąd HTTP ${response.status}: ${response.statusText}`,
      };
    }
  }

  private async handleResponse<T>(
    response: Response,
    customHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const errorData = await this.parseErrorBody(response);
      this.invalidateStoredAuthIfExpired(
        response,
        errorData,
        this.hasAuthorizationOverride(customHeaders),
      );

      const apiError: ApiRequestError = {
        status: response.status,
        error: errorData.error,
        message:
          errorData.message || 'Wystąpił błąd podczas przetwarzania żądania',
        details: errorData.details,
        errors: errorData.errors,
      };
      throw apiError;
    }

    if (response.status === 204) {
      return {
        data: null as T,
        message: response.statusText,
        status: response.status,
      };
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;

    return {
      data: data as T,
      message: response.statusText,
      status: response.status,
    };
  }

  private buildUrl(endpoint: string, params?: Record<string, string>): string {
    let url = `${this.baseUrl}${endpoint}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, value);
      });
      url += `?${searchParams.toString()}`;
    }
    return url;
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string>,
    customHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params);

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(customHeaders),
    });

    return this.handleResponse<T>(response, customHeaders);
  }

  async post<T, D = unknown>(
    endpoint: string,
    data: D,
    params?: Record<string, string>,
    customHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params);

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(customHeaders),
      body: data === undefined ? undefined : JSON.stringify(data),
    });

    return this.handleResponse<T>(response, customHeaders);
  }

  async patch<T, D = unknown>(
    endpoint: string,
    data: D,
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async getBlob(
    endpoint: string,
    params?: Record<string, string>,
    customHeaders?: Record<string, string>,
  ): Promise<{ blob: Blob; filename: string | null; status: number }> {
    const url = this.buildUrl(endpoint, params);

    const baseAuthHeader = this.accessToken
      ? { Authorization: `Bearer ${this.accessToken}` }
      : {};

    // `customHeaders` ma priorytet nad domyślnym `Authorization`, żeby
    // móc wstrzyknąć ad-hoc token (quick-access z `?token=` w URL-u)
    // bez nadpisywania sesyjnego tokenu w `sessionStorage`.
    const response = await fetch(url, {
      method: 'GET',
      headers: { [CLIENT_TYPE_HEADER]: CLIENT_TYPE_WEB, ...baseAuthHeader, ...customHeaders },
    });

    if (!response.ok) {
      const errorData = await this.parseErrorBody(response);
      this.invalidateStoredAuthIfExpired(
        response,
        errorData,
        this.hasAuthorizationOverride(customHeaders),
      );
      const apiError: ApiRequestError = {
        status: response.status,
        error: errorData.error,
        message:
          errorData.message || 'Wystąpił błąd podczas pobierania pliku',
        details: errorData.details,
      };
      throw apiError;
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    const filename = extractFilename(disposition);

    return { blob, filename, status: response.status };
  }
}

function extractFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
  }
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch) {
    return plainMatch[1].trim();
  }
  return null;
}

export const apiService = new ApiService();
